// src/pages/NearbyPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, getDocs, query, where, limit } from 'firebase/firestore';
import { db } from '../firebase';
import BookCard from '../components/BookCard';
import { getResponsivePageSize } from '../utils/pagination';
import { isReservationActive, listReservationsByBookIds } from '../utils/reservations';
import { loadBookTemplatesByIds } from '../utils/templateCache';
const KYIV_DISTRICTS = [
  { slug: 'shevchenkivskyi', label: 'Шевченківський' },
  { slug: 'pecherskyi', label: 'Печерський' },
  { slug: 'solomianskyi', label: 'Солом’янський' },
  { slug: 'holosiivskyi', label: 'Голосіївський' },
  { slug: 'svjatoshynskyi', label: 'Святошинський' },
  { slug: 'obolonskyi', label: 'Оболонський' },
  { slug: 'podilskyi', label: 'Подільський' },
  { slug: 'dniprovskyi', label: 'Дніпровський' },
  { slug: 'desnianskyi', label: 'Деснянський' },
  { slug: 'darniytskyi', label: 'Дарницький' },
];

const IN_BATCH = 10;

export default function NearbyPage() {
  const pageSize = getResponsivePageSize();
  const [params, setParams] = useSearchParams();
  const [district, setDistrict] = useState(params.get('district') || 'shevchenkivskyi');

  const [cafeIds, setCafeIds] = useState([]);
  const [batchIdx, setBatchIdx] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const label = useMemo(
    () => KYIV_DISTRICTS.find((d) => d.slug === district)?.label ?? '',
    [district]
  );

  useEffect(() => { setParams({ district }); }, [district, setParams]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setItems([]);
      setBatchIdx(0);
      setHasMore(true);

      const cafesSnap = await getDocs(
        query(collection(db, 'cafes'), where('districtSlug', '==', district))
      );
      const ids = cafesSnap.docs.map((d) => d.id);
      setCafeIds(ids);

      if (!ids.length) {
        setLoading(false);
        setHasMore(false);
        return;
      }

      await loadBatch(ids, 0, true);
      setLoading(false);
    })();
  }, [district]);

  const loadBatch = async (ids, idx, replace = false) => {
    const start = idx * IN_BATCH;
    const slice = ids.slice(start, start + IN_BATCH);
    if (!slice.length) {
      setHasMore(false);
      return;
    }

    const snap = await getDocs(
      query(
        collection(db, 'books'),
        where('status', '==', 'available'),
        where('cafeId', 'in', slice),
        limit(pageSize)
      )
    );

    const books = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const reservations = await listReservationsByBookIds(books.map((b) => b.id));
    const reservedBookIds = new Set(
      reservations.filter((r) => isReservationActive(r)).map((r) => String(r.bookId || r.id))
    );

    // Merge metadata from templates to ensure covers/titles/authors are present.
    const visibleBooks = books.filter((b) => !reservedBookIds.has(String(b.id)));
    const templateIds = Array.from(new Set(visibleBooks.map((b) => b.templateId).filter(Boolean)));
    const templates = await loadBookTemplatesByIds(templateIds);
    const tplMap = new Map(templates.map((t) => [t.id, t]));

    const mergedBooks = visibleBooks.map((b) => {
      const t = tplMap.get(b.templateId) || {};
      return {
        ...b,
        isbn: b.isbn || t.isbn || '',
        title: b.title || t.title || '',
        author: b.author || t.author || '',
        coverUrl: b.coverUrl || t.coverUrl || '',
        borrowPrice: b.borrowPrice ?? b.priceBorrow ?? b.price ?? t.borrowPrice ?? t.priceBorrow ?? t.price ?? null,
        borrowCount: b.borrowCount ?? t.borrowCount ?? 0,
      };
    });

    if (replace) setItems(mergedBooks);
    else setItems((prev) => [...prev, ...mergedBooks]);

    if (mergedBooks.length < pageSize) {
      setBatchIdx(idx + 1);
      if ((idx + 1) * IN_BATCH >= ids.length) setHasMore(false);
    } else {
      setBatchIdx(idx);
    }
  };

  const onMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    await loadBatch(cafeIds, batchIdx, false);
    setLoadingMore(false);
  };

  const [winW, setWinW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const onResize = () => setWinW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const cols =
    winW <= 540 ? 'repeat(2, minmax(0,1fr))' :
    winW <= 900 ? 'repeat(3, minmax(0,1fr))' :
                  'repeat(4, minmax(0,1fr))';

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>Книги у районі: {label}</h1>

        <select
          value={district}
          onChange={(e) => setDistrict(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #D1D5DB' }}
        >
          {KYIV_DISTRICTS.map((d) => <option key={d.slug} value={d.slug}>{d.label}</option>)}
        </select>
      </div>

      {loading && <p>Завантаження...</p>}
      {!loading && !items.length && <p>Наразі немає доступних книг у цьому районі.</p>}

      <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 16 }}>
        {items.map((b) => <BookCard key={b.id} book={b} />)}
      </div>

      {!loading && (
        <div style={{ textAlign: 'center', margin: '20px 0' }}>
          {hasMore ? (
            <button
              onClick={onMore}
              disabled={loadingMore}
              style={{ padding: '10px 16px', borderRadius: 10, border: '1px solid #0b5226', background: '#0b5226', color: '#fff', fontWeight: 700 }}
            >
              {loadingMore ? 'Завантаження...' : 'Показати ще'}
            </button>
          ) : (items.length > 0 && <div style={{ color: '#6b7280' }}>Усе показано</div>)}
        </div>
      )}
    </div>
  );
}


